import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const { userId: otherUserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [error, setError] = useState('');
  const [matchLost, setMatchLost] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch other user's profile info
  useEffect(() => {
    const fetchOtherUser = async () => {
      try {
        const response = await axios.get(`${API}/profiles/${otherUserId}`, { withCredentials: true, headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) } });
        setOtherUser(response.data);
      } catch {
        setError('No se pudo cargar el perfil');
      }
    };
    fetchOtherUser();
  }, [otherUserId]);

  // Fetch messages
  const fetchMessages = async (isPolling = false) => {
    try {
      const response = await axios.get(`${API}/messages/${otherUserId}`, { withCredentials: true, headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) } });
      setMessages(response.data.messages);
      if (!isPolling && response.data.messages?.length > 0) {
          const lastMsg = response.data.messages[response.data.messages.length - 1];
          localStorage.setItem(`lastReadMsg_${otherUserId}`, lastMsg.created_at);
        }
      if (!isPolling) setError('');
    } catch (err) {
      if (err.response?.status === 403) {
        setMatchLost(true);
        setError('Este match ya no está disponible');
      } else if (!isPolling) {
        setError('Error al cargar mensajes');
      }
    } finally {
      if (!isPolling) setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [otherUserId]);

  // Polling: refresh messages every 5 seconds (only if match is active)
  useEffect(() => {
    if (matchLost) return;
    const interval = setInterval(() => fetchMessages(true), 5000);
    return () => clearInterval(interval);
  }, [otherUserId, matchLost]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, { receiver_id: otherUserId, text }, { withCredentials: true, headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) } });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Hoy';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const dateKey = new Date(msg.created_at).toDateString();
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(144,18%,45%)]" />
        </div>
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/matches')}
            className="text-[hsl(144,18%,45%)] hover:underline"
          >
            Volver a contactos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Chat header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/matches')}
            className="text-gray-500 hover:text-gray-700"
            data-testid="chat-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {otherUser && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[hsl(144,18%,90%)] flex items-center justify-center text-[hsl(144,18%,40%)] font-bold text-sm">
                {otherUser.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="font-medium text-gray-900" data-testid="chat-other-name">
                  {otherUser.name}
                </p>
                {otherUser.city && (
                  <p className="text-xs text-gray-500">{otherUser.city}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="messages-container">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <p className="text-center text-gray-400 py-16" data-testid="no-messages">
              No hay mensajes aún. ¡Envía el primero!
            </p>
          )}

          {Object.entries(groupedMessages).map(([dateKey, msgs]) => (
            <div key={dateKey}>
              <div className="flex justify-center my-4">
                <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                  {formatDate(msgs[0].created_at)}
                </span>
              </div>
              {msgs.map((msg, i) => {
                const isMine = msg.sender_id === user?.user_id;
                return (
                  <div
                    key={`${msg.created_at}-${i}`}
                    className={`flex mb-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${i}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        isMine
                          ? 'bg-[hsl(144,18%,45%)] text-white rounded-br-md'
                          : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                      }`}
                    >
                      <p>{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-white/70' : 'text-gray-400'}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area or match-lost banner */}
      {matchLost ? (
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-4">
          <p className="max-w-3xl mx-auto text-center text-sm text-gray-500" data-testid="match-lost-message">
            Este match ya no está disponible. No puedes enviar ni leer mensajes.
          </p>
          <div className="max-w-3xl mx-auto text-center mt-2">
            <button
              onClick={() => navigate('/matches')}
              className="text-sm text-[hsl(144,18%,45%)] hover:underline"
            >
              Volver a contactos
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white border-t border-gray-200 px-4 py-3">
          <form onSubmit={handleSend} className="max-w-3xl mx-auto flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Escribe un mensaje..."
              className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(144,18%,80%)] focus:border-[hsl(144,18%,45%)]"
              disabled={sending}
              data-testid="message-input"
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="px-4 py-2.5 bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          {error && messages.length > 0 && (
            <p className="max-w-3xl mx-auto text-xs text-red-500 mt-1">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}
